/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { router } from 'expo-router'
import isEqual from 'lodash/isEqual'
import numeral from 'numeral'
import { useState } from 'react'
import { Toast } from 'react-native-toast-universal'
import { states } from 'shared'
import { Button, ButtonGroup, Checkbox, colorWithBrightness, colorWithOpacity, MultiSelect, Panel, Select, Spacing, Table, Text, useTheme, Wrapper } from 'ui'
import { useMediaQuery } from 'usehooks-ts'
import { Drawer } from '@/components/web/Drawer'
import { Head } from '@/components/web/Head'
import { NavButtons } from '@/components/web/NavButtons'
import { Constants } from '@/constants'
import { defaultAccidentFilter, useStore } from '@/providers/store'
import { useAccidents, useAccidentsExport } from '@/queries/accident.queries'
import { useConfig } from '@/queries/config.queries'
import { useLawFirmContacts, useLawFirms } from '@/queries/law-firm.queries'
import { useLocations, useProviders } from '@/queries/provider.queries'
import { useSaveAccidentFilter, useUser, useUsers } from '@/queries/user.queries'
import { AccidentExport, AccidentExportCommission, AccidentExportEntity, AccidentExportReplacements, AccidentExportType, AccidentListItem, AccidentStatusSchema, simplifiedStatus } from '@/types/accident'
import { AccidentFilter } from '@/types/filter'
import { statusers, userPermissions } from '@/types/user'
import { format } from '@/utils'

export function Accidents() {
  const theme = useTheme()
  const filter = useStore.useAccidentFilter()
  const setFilter = useStore.useSetAccidentFilter()

  const user = useUser()
  const accidents = useAccidents(filter)
  const saveAccidentFilter = useSaveAccidentFilter()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.m}px)`)
  if (isDesktop && drawerOpen) setDrawerOpen(false)

  function updateFilter(filter: AccidentFilter) {
    setFilter(filter)
    saveAccidentFilter.mutate(filter)
  }

  const styles = {
    wrapper: css({
      display: 'flex',
    }),
    content: css({
      flex: 1,
      margin: `${theme.spacing * 1.5}px ${theme.spacing}px`,
      overflowX: 'auto',
      [`@media (min-width: ${theme.breakpoints.m}px)`]: {
        margin: theme.spacing * 1.5,
      },
    }),
  }

  return <>
    <Head title='Cases' />

    <div css={styles.wrapper}>
      <Sidebar onUpdateFilter={updateFilter} />

      <div css={styles.content}>
        <Wrapper maxWidth={user.data?.role === 'provider' ? 1400 : 1800} margin={false}>
          <Header
            count={accidents.data ? accidents.data.accidents.length : undefined}
            total={accidents.data ? accidents.data.total : undefined}
            onUpdateFilter={updateFilter}
            onToggleSidebar={() => setDrawerOpen(true)}
          />
          <Panel>
            <AccidentsTable
              accidents={accidents.data?.accidents ?? []}
              loading={accidents.isPending}
              onUpdateFilter={updateFilter}
            />
          </Panel>
        </Wrapper>
      </div>
    </div>

    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
      <Sidebar inDrawer={true} onUpdateFilter={updateFilter} />
    </Drawer>
  </>
}

function Header({ count: propCount, total: propTotal, onUpdateFilter, onToggleSidebar }: {
  count: number | undefined
  total: number | undefined
  onUpdateFilter: (filter: AccidentFilter) => void
  onToggleSidebar: () => void
}) {
  const theme = useTheme()
  const filter = useStore.useAccidentFilter()

  const user = useUser()
  const accidentsExport = useAccidentsExport()

  const [count, setCount] = useState(propCount)
  const [prevCount, setPrevCount] = useState(propCount)
  if (propCount !== prevCount && propCount !== undefined) {
    setCount(propCount)
    setPrevCount(propCount)
  }
  const [total, setTotal] = useState(propTotal)
  const [prevTotal, setPrevTotal] = useState(propTotal)
  if (propTotal !== prevTotal && propTotal !== undefined) {
    setTotal(propTotal)
    setPrevTotal(propTotal)
  }

  const offset = (filter.page - 1) * filter.show
  const isSingleProvider = user.data?.role === 'provider' && user.data.providers.length === 1

  const styles = {
    wrapper: css({
      background: theme.colors.pageBackground,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      columnGap: theme.spacing,
      marginBottom: theme.spacing,
    }),
    heading: css({
      display: 'flex',
      alignItems: 'center',
      columnGap: theme.spacing,
      rowGap: theme.spacing * 0.2,
      flexWrap: 'wrap',
    }),
    info: css({
      fontFamily: theme.fonts.body[400],
      fontSize: 14.5,
      opacity: 0.8,
    }),
    buttons: css({
      display: 'flex',
      alignItems: 'center',
      columnGap: theme.spacing * 0.5,
    }),
    sidebarToggle: css({
      [`@media (min-width: ${theme.breakpoints.m}px)`]: {
        display: 'none',
      },
    }),
  }

  return (
    <div css={styles.wrapper}>
      <div css={styles.heading}>
        <Text type='heading'>Cases</Text>
        <div css={styles.info}>
          {count !== undefined ? `Showing ${count > 1 ? `${offset + 1} - ${Math.min(offset + count, total ?? 0)}` : count}` : ''}
          {(count !== undefined && total !== undefined && total > count) ? ` (of ${numeral(total).format()})` : ''}
        </div>
      </div>

      <div css={styles.buttons}>
        <Button
          text='Export'
          type='tertiary'
          compact={true}
          loading={isSingleProvider && accidentsExport.isPending}
          textLeft={(
            <FontAwesomeIcon
              icon={['far', 'file-export']}
              size={15}
              color={colorWithOpacity(theme.colors.foreground, 0.9)}
              style={{ marginRight: 7 }}
            />
          )}
          onPress={() => {
            if (isSingleProvider) {
              downloadExport(accidentsExport, filter, { entity: 'accident', type: 'current', replacements: 'include', commission: 'exclude' })
            } else {
              useStore.getState().modal.open({
                title: 'Export',
                content: <Export />,
              })
            }
          }}
        />
        <NavButtons
          prev={{
            disabled: filter.page === 1,
            onClick: () => onUpdateFilter({ ...filter, page: filter.page - 1 }),
          }}
          next={{
            disabled: count === undefined || total === undefined ? true : offset + count >= total,
            onClick: () => onUpdateFilter({ ...filter, page: filter.page + 1 }),
          }}
        />
        <div css={styles.sidebarToggle}>
          <Button
            text=''
            type='tertiary'
            compact={true}
            textLeft={(
              <FontAwesomeIcon
                icon={['far', 'sidebar']}
                size={16}
                color={colorWithOpacity(theme.colors.foreground, 0.9)}
              />
            )}
            onPress={onToggleSidebar}
          />
        </div>
      </div>
    </div>
  )
}

function Export() {
  const theme = useTheme()
  const filter = useStore.useAccidentFilter()

  const user = useUser()
  const accidentsExport = useAccidentsExport()

  const [entity, setEntity] = useState<AccidentExportEntity>(user.data?.role === 'external' ? 'account' : 'accident')
  const [type, setType] = useState<AccidentExportType>('current')
  const [replacements, setReplacements] = useState<AccidentExportReplacements>(user.data?.role === 'provider' ? 'include' : 'exclude')
  const [commission, setCommission] = useState<AccidentExportCommission>('exclude')

  const styles = {
    wrapper: css({
      width: `min(420px, 100dvw - ${theme.spacing * 2}px)`,
      padding: `0 ${theme.spacing * 1.4}px`,
    }),
    toggles: css({
      display: 'flex',
      flexDirection: 'column',
      rowGap: theme.spacing,
    }),
  }

  return (
    <div css={styles.wrapper}>
      <Spacing height={1.2} />
      <div css={styles.toggles}>
        {user.data?.role !== 'external' && (
          <ButtonGroup
            label='Type'
            fullWidth={true}
            compact={true}
            buttons={[
              {
                text: 'Cases',
                active: entity === 'accident',
                onPress: () => setEntity('accident'),
              },
              {
                text: 'Accounts',
                active: entity === 'account',
                onPress: () => setEntity('account'),
              },
              ...((user.data?.role === 'finance' || user.data?.role === 'executive') ? [{
                text: 'Transactions',
                active: entity === 'transaction',
                onPress: () => setEntity('transaction'),
              }] : []),
            ]}
          />
        )}
        {user.data?.role !== 'provider' && entity !== 'transaction' && (
          <ButtonGroup
            label='Columns'
            fullWidth={true}
            compact={true}
            buttons={[
              {
                text: 'Current',
                active: type === 'current',
                onPress: () => setType('current'),
              },
              {
                text: 'All',
                active: type === 'full',
                onPress: () => setType('full'),
              },
            ]}
          />
        )}
        {(user.data?.role !== 'external' && user.data?.role !== 'provider') && (
          <ButtonGroup
            label='Replacement Cases'
            fullWidth={true}
            compact={true}
            buttons={[
              {
                text: 'Exclude',
                active: replacements === 'exclude',
                onPress: () => setReplacements('exclude'),
              },
              {
                text: 'Include',
                active: replacements === 'include',
                onPress: () => setReplacements('include'),
              },
            ]}
          />
        )}
        {user.data?.role === 'finance' && entity !== 'transaction' && (
          <ButtonGroup
            label='Commission'
            fullWidth={true}
            compact={true}
            buttons={[
              {
                text: 'Exclude',
                active: commission === 'exclude',
                onPress: () => setCommission('exclude'),
              },
              {
                text: 'Include',
                active: commission === 'include',
                onPress: () => setCommission('include'),
              },
            ]}
          />
        )}
      </div>
      <Spacing height={1.4} />
      <Button
        text='Export'
        type='primary'
        loading={accidentsExport.isPending}
        onPress={() => downloadExport(accidentsExport, filter, { entity, type, replacements, commission }, { closeModal: true })}
      />
      <Spacing height={1.4} />
    </div>
  )
}

function downloadExport(
  accidentsExport: ReturnType<typeof useAccidentsExport>,
  filter: AccidentFilter,
  exportParams: AccidentExport,
  options?: { closeModal?: boolean },
) {
  const label = { accident: 'Cases', account: 'Accounts', transaction: 'Transactions' }[exportParams.entity]
  accidentsExport.mutate({ filter, exportParams }, {
    onSuccess: (res) => {
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(new Blob([res.data]))
      link.setAttribute('download', `${label.toLowerCase()}-export.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      Toast.success(`${label} exported`)
      if (options?.closeModal) useStore.getState().modal.close()
    },
    onError: () => Toast.error(`Unable to export ${label.toLowerCase()}`),
  })
}

function AccidentsTable({ accidents, loading, onUpdateFilter }: {
  accidents: AccidentListItem[]
  loading: boolean
  onUpdateFilter: (filter: AccidentFilter) => void
}) {
  const filter = useStore.useAccidentFilter()
  const clearFilter = useStore.useClearAccidentFilter()

  const user = useUser()
  const saveAccidentFilter = useSaveAccidentFilter()

  return (
    <Table
      columns={[
        {
          data: 'provider',
          title: 'Provider',
          width: 230,
          hidden: user.data?.role === 'provider' && user.data.providers.length === 1,
        },
        {
          data: 'name',
          title: user.data && userPermissions(user.data).canViewPatientInfo ? 'Name' : 'Case',
          width: user.data && userPermissions(user.data).canViewPatientInfo ? 220 : 160,
          sortable: true,
        },
        {
          data: 'status',
          title: 'Status',
          width: 230,
          sortable: user.data && userPermissions(user.data).canFilterByAccidentStatus,
          style: { whiteSpace: 'nowrap' },
        },
        {
          data: 'lastContact',
          title: 'Last Contact',
          width: 140,
          sortable: true,
        },
        {
          data: 'nextContact',
          title: 'Next Contact',
          width: 140,
          sortable: true,
        },
        {
          data: 'lawFirm',
          title: 'Law Firm',
          width: 250,
          sortable: true,
        },
        {
          data: 'target',
          title: 'Target',
          width: 122,
          alignRight: true,
          sortable: true,
          hidden: user.data?.role === 'provider',
        },
        {
          data: 'invoices',
          title: 'Invoices',
          width: 122,
          alignRight: true,
          sortable: true,
        },
        {
          data: 'collected',
          title: 'Collected',
          width: 122,
          alignRight: true,
          sortable: true,
          hidden: user.data?.role === 'provider',
        },
        {
          data: 'cost',
          title: 'Cost',
          width: 122,
          alignRight: true,
          sortable: true,
          hidden: user.data?.role === 'provider',
        },
      ]}
      data={accidents.map((accident) => {
        return {
          id: accident.id,
          provider: accident.accounts.map((a) => a.provider.name).join(', '),
          name: user.data && userPermissions(user.data).canViewPatientInfo ? accident.patient.fullName : `Case #${accident.id}`,
          status: user.data?.role === 'external' ? simplifiedStatus(accident.status) : accident.status,
          lastContact: accident.lastContact ? format.date(accident.lastContact) : '',
          nextContact: accident.nextContact ? format.date(accident.nextContact) : '',
          lawFirm: accident.lawFirm?.name ?? '',
          target: accident.target != null ? format.currency(accident.target) : '',
          invoices: accident.invoices != null ? format.currency(accident.invoices) : '',
          collected: accident.collected != null ? format.currency(accident.collected) : '',
          cost: accident.cost != null ? format.currency(accident.cost) : '',
        }
      })}
      loading={loading}
      noDataMessage='No cases found'
      noDataButton={(
        <Button
          text='Clear Filters'
          type='tertiary'
          compact={true}
          onPress={() => {
            const cleared = clearFilter(filter)
            saveAccidentFilter.mutate(cleared)
          }}
        />
      )}
      fixedLayout={true}
      stickyHeader={true}
      orderBy={filter.orderBy}
      sortOrder={filter.sortOrder}
      onUpdateSort={({ orderBy, sortOrder }) => onUpdateFilter({ ...filter, orderBy, sortOrder, page: 1 })}
      onRowClick={(row) => router.navigate(`/cases/${row.id}`)}
    />
  )
}

function Sidebar({ inDrawer, onUpdateFilter }: { inDrawer?: boolean, onUpdateFilter: (filter: AccidentFilter) => void }) {
  const theme = useTheme()
  const filter = useStore.useAccidentFilter()

  const user = useUser()
  const users = useUsers()
  const locations = useLocations()
  const providers = useProviders(filter.locations)
  const lawFirms = useLawFirms(filter.locations, filter.providers)
  const lawFirmContacts = useLawFirmContacts(filter.lawFirms)
  const clearFilter = useStore.useClearAccidentFilter()
  const saveAccidentFilter = useSaveAccidentFilter()

  const styles = {
    sidebar: css({
      width: 320,
      ...(!inDrawer && {
        height: '100%',
        minHeight: `calc(100vh - ${Constants.headerHeight}px)`,
        background: theme.colors.headerBackground,
        borderRight: `1px solid ${theme.colors.panelBorder}`,
      }),
      padding: `${theme.spacing * 1.5}px ${theme.spacing}px`,
      display: inDrawer ? 'flex' : 'none',
      flexDirection: 'column',
      rowGap: theme.spacing,
      ...(!inDrawer && {
        [`@media (min-width: ${theme.breakpoints.m}px)`]: {
          display: 'flex',
        },
      }),
    }),
    divider: css({
      height: 1,
      background: theme.colors.panelBorder,
      margin: `0 -${theme.spacing}px`,
    }),
  }

  return (
    <div css={styles.sidebar}>
      {user.data?.role !== 'provider' && (
        <MultiSelect
          label='State'
          value={filter.locations}
          options={locations.data?.map((l) => ({ value: l, label: states[l] })) ?? []}
          placeholder='Select state(s)'
          noOptions='State not found'
          compact={true}
          onChange={(value) => onUpdateFilter({ ...filter, locations: value, providers: [], lawFirms: [], lawFirmContacts: [], page: 1 })}
        />
      )}
      {(user.data?.role !== 'provider' || (providers.data && providers.data?.length > 1)) && (
        <MultiSelect
          label='Provider'
          value={filter.providers}
          options={providers.data?.map((p) => ({ value: p.id.toString(), label: p.name })) ?? []}
          placeholder='Select provider(s)'
          noOptions='Provider not found'
          compact={true}
          onChange={(value) => onUpdateFilter({ ...filter, providers: value, lawFirms: [], lawFirmContacts: [], page: 1 })}
        />
      )}
      <MultiSelect
        label='Law Firm'
        value={filter.lawFirms}
        options={lawFirms.data?.map((l) => ({ value: l.id.toString(), label: l.name })) ?? []}
        placeholder='Select law firm(s)'
        noOptions='Law firm not found'
        compact={true}
        onChange={(value) => onUpdateFilter({ ...filter, lawFirms: value, lawFirmContacts: [], page: 1 })}
      />
      <MultiSelect
        label='Law Firm Contact'
        value={filter.lawFirmContacts}
        options={lawFirmContacts.data?.map((c) => ({ value: c.id.toString(), label: c.displayName })) ?? []}
        placeholder='Select law firm contact(s)'
        noOptions='No contacts found'
        compact={true}
        disabled={filter.lawFirms.length === 0}
        onChange={(value) => onUpdateFilter({ ...filter, lawFirmContacts: value, page: 1 })}
      />
      {user.data && userPermissions(user.data).canFilterByAccidentStatus ? (
        <MultiSelect
          label='Status'
          value={filter.statuses}
          options={AccidentStatusSchema.options}
          placeholder='Select status(es)'
          noOptions='Status not found'
          compact={true}
          onChange={(value) => onUpdateFilter({ ...filter, statuses: value, page: 1 })}
        />
      ) : (
        <Select
          label='Status'
          value={filter.openSettled}
          options={['All Cases', 'Open', 'Settled']}
          compact={true}
          onChange={(value) => onUpdateFilter({ ...filter, openSettled: value, page: 1 })}
        />
      )}
      <Checkbox
        label='Documents'
        text='Missing Documents'
        value={filter.missingDocuments}
        compact={true}
        onChange={(value) => onUpdateFilter({ ...filter, missingDocuments: value, page: 1 })}
      />
      {(user.data?.role === 'staff' || user.data?.role === 'executive' || user.data?.role === 'finance') && (
        <MultiSelect
          label='Assigned Staff'
          value={filter.statusers}
          options={statusers(users.data ?? []).map((u) => ({ value: u.id.toString(), label: u.fullName ?? '' }))}
          placeholder='Select staff'
          noOptions='Staff not found'
          compact={true}
          onChange={(value) => onUpdateFilter({ ...filter, statusers: value, page: 1 })}
        />
      )}
      {(user.data?.role === 'staff' || user.data?.role === 'executive' || user.data?.role === 'finance') && (
        <Tags onUpdateFilter={onUpdateFilter} />
      )}
      <div css={styles.divider} />
      <Button
        text='Clear Filters'
        type='tertiary'
        compact={true}
        disabled={isEqual(filter, defaultAccidentFilter)}
        onPress={() => {
          const cleared = clearFilter(filter)
          saveAccidentFilter.mutate(cleared)
        }}
      />
    </div>
  )
}

function Tags({ onUpdateFilter }: { onUpdateFilter: (filter: AccidentFilter) => void }) {
  const TAG_HEIGHT = 22

  const theme = useTheme()
  const filter = useStore.useAccidentFilter()

  const config = useConfig()

  const styles = {
    wrapper: css({
      background: colorWithBrightness(theme.colors.headerBackground, -7),
      boxShadow: `inset 1px 0 3px ${colorWithOpacity('#000000', 0.1)}`,
      margin: `0 -${theme.spacing}px`,
      padding: theme.spacing,
      display: 'flex',
      flexWrap: 'wrap',
      gap: 7,
    }),
    tag: css({
      height: TAG_HEIGHT,
      borderRadius: TAG_HEIGHT,
      lineHeight: `${TAG_HEIGHT}px`,
      padding: '0 9px',
      color: '#FFFFFF',
      fontFamily: theme.fonts.body[600],
      fontSize: 10,
      letterSpacing: 0.2,
      textTransform: 'uppercase',
      cursor: 'pointer',
    }),
  }

  return (
    <div css={styles.wrapper}>
      {config.data?.tags.map((tag) => (
        <div
          key={tag.name}
          css={styles.tag}
          style={{
            background: tag.color,
            opacity: filter.tags.includes(tag.name) ? 1 : 0.5,
          }}
          onClick={() => {
            let newTags = [...filter.tags]
            if (newTags.includes(tag.name)) {
              newTags = newTags.filter((t) => t !== tag.name)
            } else {
              newTags.push(tag.name)
            }
            onUpdateFilter({ ...filter, tags: newTags, page: 1 })
          }}
        >
          {tag.name}
        </div>
      ))}
    </div>
  )
}
